// Date-range + bucketing helpers for the Overview analytics filters.
// Relies on the "Created At" field the backend exposes from the groups.created_at column.
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  subDays,
  subWeeks,
  subMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  format,
  isValid,
  isWithinInterval,
  isSameDay,
  differenceInCalendarDays,
} from "date-fns";

export const GRANULARITIES = ["Day", "Week", "Month"];

export function parseCreatedAt(row) {
  const raw = row?.["Created At"];
  if (!raw) return null;
  const d = new Date(raw);
  return isValid(d) ? d : null;
}

export function inRange(date, range) {
  if (!date || !range?.from || !range?.to) return false;
  return isWithinInterval(date, { start: range.from, end: range.to });
}

export function defaultRangeFor(granularity) {
  const today = endOfDay(new Date());
  if (granularity === "Day") return { from: startOfDay(subDays(today, 13)), to: today };
  if (granularity === "Week") return { from: startOfWeek(subWeeks(today, 7), { weekStartsOn: 1 }), to: today };
  return { from: startOfMonth(subMonths(today, 5)), to: today };
}

export function presets() {
  const now = new Date();
  return [
    { label: "Today", range: { from: startOfDay(now), to: endOfDay(now) } },
    { label: "This week", range: { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) } },
    { label: "This month", range: { from: startOfMonth(now), to: endOfDay(now) } },
    { label: "Last 7 days", range: { from: startOfDay(subDays(now, 6)), to: endOfDay(now) } },
    { label: "Last 30 days", range: { from: startOfDay(subDays(now, 29)), to: endOfDay(now) } },
  ];
}

function bucketStart(date, granularity) {
  if (granularity === "Day") return startOfDay(date);
  if (granularity === "Week") return startOfWeek(date, { weekStartsOn: 1 });
  return startOfMonth(date);
}

function bucketKey(date, granularity) {
  if (granularity === "Day") return format(date, "yyyy-MM-dd");
  if (granularity === "Week") return format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-'W'II");
  return format(date, "yyyy-MM");
}

function bucketLabel(date, granularity) {
  if (granularity === "Day") return format(date, "MMM d");
  if (granularity === "Week") return format(startOfWeek(date, { weekStartsOn: 1 }), "MMM d");
  return format(date, "MMM yyyy");
}

export function bucketsInRange(range, granularity) {
  if (!range?.from || !range?.to) return [];
  if (granularity === "Day") return eachDayOfInterval({ start: range.from, end: range.to });
  if (granularity === "Week") return eachWeekOfInterval({ start: range.from, end: range.to }, { weekStartsOn: 1 });
  return eachMonthOfInterval({ start: range.from, end: range.to });
}

// Counts of `predicate`-matching rows per bucket across `range`; empty buckets included as 0.
export function buildSeries(rows, range, granularity, predicate = () => true) {
  const buckets = bucketsInRange(range, granularity);
  const counts = new Map(buckets.map((d) => [bucketKey(d, granularity), 0]));
  rows.forEach((r) => {
    const d = parseCreatedAt(r);
    if (!d || !inRange(d, range) || !predicate(r)) return;
    const k = bucketKey(bucketStart(d, granularity), granularity);
    if (counts.has(k)) counts.set(k, counts.get(k) + 1);
  });
  return buckets.map((d) => ({
    key: bucketKey(d, granularity),
    label: bucketLabel(d, granularity),
    v: counts.get(bucketKey(d, granularity)) || 0,
  }));
}

// Equal-length period immediately preceding `range`, for a comparison "ghost" series.
export function previousPeriod(range) {
  if (!range?.from || !range?.to) return null;
  const days = differenceInCalendarDays(range.to, range.from) + 1;
  return { from: subDays(range.from, days), to: subDays(range.to, days) };
}

// yyyy-MM-dd -> count, for the calendar heatmap. Independent of the active range/granularity.
export function dailyActivityMap(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const d = parseCreatedAt(r);
    if (!d) return;
    const k = format(d, "yyyy-MM-dd");
    map.set(k, (map.get(k) || 0) + 1);
  });
  return map;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function formatRangeParam(date) {
  return format(date, "yyyy-MM-dd");
}

function parseRangeParam(value) {
  if (!value || !ISO_DATE.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return isValid(d) ? d : null;
}

// `from` / `to` query params (yyyy-MM-dd) carried from Overview into queue detail pages.
export function rangeFromSearchParams(searchParams) {
  const from = parseRangeParam(searchParams.get("from"));
  const to = parseRangeParam(searchParams.get("to"));
  if (!from || !to) return null;
  return { from: startOfDay(from), to: endOfDay(to) };
}

export function formatRangeChipLabel(range) {
  if (!range?.from || !range?.to) return "";
  if (isSameDay(range.from, range.to)) return format(range.from, "MMM d, yyyy");
  return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`;
}

export function buildQueueSearchParams({ location, range, hasDates }) {
  const params = new URLSearchParams();
  if (location && location !== "All Locations") params.set("location", location);
  if (hasDates && range?.from && range?.to) {
    params.set("from", formatRangeParam(range.from));
    params.set("to", formatRangeParam(range.to));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function filterRowsByCreatedRange(rows, range) {
  if (!range?.from || !range?.to) return rows;
  return rows.filter((r) => inRange(parseCreatedAt(r), range));
}

export function searchParamsWithoutLocation(searchParams) {
  const params = new URLSearchParams(searchParams);
  params.delete("location");
  return params;
}

export function searchParamsWithoutRange(searchParams) {
  const params = new URLSearchParams(searchParams);
  params.delete("from");
  params.delete("to");
  return params;
}
